import { GeneratedJobType } from 'components/JobGenerator/types';
import { DraftType } from 'components/RightSidebar/types';
import { FormattedObjectType, JobsTypes } from 'components/Sidebar/types';
import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { jobPermissionError, notJobMessage } from 'helpers/messages';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { AppDispatch } from 'store';
import { setAdditionalSettings } from 'store/global/globalSlice';
import { FiltersType, SearchHistoryType } from 'store/global/types';
import {
    ADDITIONAL_SETTINGS,
    CONVERSATIONS,
    DRAFTS,
    SEARCH_HISTORY,
} from 'utils/config/constants/controllerNames';
import { auth, db } from 'utils/config/firebase';
import { errorLogger } from 'utils/config/firebase/logs';

export const getContextQuery = (
    doc_id: string,
    setItem: Dispatch<SetStateAction<GeneratedJobType | null>>,
) => {
    try {
        const unsubscribe = onSnapshot(doc(db, CONVERSATIONS, doc_id), (doc) => {
            const data = doc.data() as GeneratedJobType;
            if (!data) {
                notJobMessage();
            }
            if (auth?.currentUser?.uid === data?.author_id) {
                setItem(data);
            } else {
                jobPermissionError();
            }
        });
        return unsubscribe;
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getContextQuery');
        }
        console.log(error);
        return null;
    }
};

export const getAllContextsQuery = (
    setData: Dispatch<SetStateAction<FormattedObjectType[]>>,
    formatData: (item: JobsTypes[]) => FormattedObjectType[],
    savedUnFormattedData: MutableRefObject<JobsTypes[]>,
) => {
    try {
        const collectionRef = collection(db, CONVERSATIONS);
        const q = query(
            collectionRef,
            where('author_id', '==', auth?.currentUser?.uid),
            where('deleted', '!=', true),
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (snapshot.docs[0]) {
                const filteredData: JobsTypes[] = snapshot.docs
                    .map((doc) => ({ ...doc.data(), document_id: doc.id }) as JobsTypes)
                    .sort(
                        (a, b) =>
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                    );
                savedUnFormattedData.current = filteredData;
                setData(formatData(filteredData));
            } else {
                setData([]);
            }
        });
        return unsubscribe;
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getAllContextsQuery');
        }
        console.log(error);
        return null;
    }
};

export const getDraftsQuery = (setDrafts: Dispatch<SetStateAction<DraftType[]>>) => {
    try {
        const collectionRef = collection(db, DRAFTS);
        const q = query(
            collectionRef,
            where('user_id', '==', auth?.currentUser?.uid),
            where('deleted', '!=', true),
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (snapshot.docs[0]) {
                const filteredData: DraftType[] = snapshot.docs
                    .map((doc) => ({
                        created_at: doc.data().created_at,
                        value: doc.data().value,
                        version_id: doc.data().version_id,
                        updated_at: doc.data().updated_at,
                        draft_value: doc.data().draft_value,
                        deleted: doc.data().deleted,
                        document_id: doc.id,
                    }))
                    .sort(
                        (a, b) =>
                            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
                    );
                setDrafts(filteredData);
            } else {
                setDrafts([]);
            }
        });

        return unsubscribe;
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getDraftsQuery');
        }
        console.log(error);
        return null;
    }
};

export const getAdditionalSettings = (dispatch: AppDispatch) => {
    try {
        const collectionRef = collection(db, ADDITIONAL_SETTINGS);
        const q = query(collectionRef);
        onSnapshot(q, (snapshot) => {
            if (snapshot.docs[0]) {
                const data = snapshot.docs[0].data() as FiltersType;
                dispatch(setAdditionalSettings(data));
            }
        });
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getAdditionalSettings');
        }
        console.log(error);
    }
};

export const getSearchHistory = (
    setSearchHistory: Dispatch<SetStateAction<SearchHistoryType[]>>,
) => {
    try {
        const collectionRef = collection(db, SEARCH_HISTORY);
        const q = query(
            collectionRef,
            where('user_id', '==', auth?.currentUser?.uid),
            orderBy('updated_at', 'desc'),
            limit(3),
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const filteredData: SearchHistoryType[] = snapshot.docs
                .map((doc) => ({
                    created_at: doc.data().created_at,
                    updated_at: doc.data().updated_at,
                    title: doc.data().title,
                    subtitle: doc.data().subtitle,
                    job_id: doc.data().job_id,
                    document_id: doc.id,
                }))
                .sort(
                    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
                );
            setSearchHistory(filteredData);
        });
        return unsubscribe;
    } catch (error) {
        if (error instanceof Error) {
            errorLogger.critical(error?.message, 'getSearchHistory');
        }
        console.log(error);
        return null;
    }
};
